export type AppBackHandler = () => boolean;

export interface AppBackHandlerOptions {
    /** Чем больше значение, тем раньше обработчик получает событие `App Back`. */
    priority?: number;
}

interface AppBackEntry {
    id: number;
    priority: number;
    handler: AppBackHandler;
}

const DEFAULT_PRIORITY = 0;

let nextId = 1;
let entries: AppBackEntry[] = [];
let routerBack: (() => void) | null = null;

function sortByPriorityAndRegistration(a: AppBackEntry, b: AppBackEntry): number {
    if (a.priority !== b.priority) {
        return b.priority - a.priority;
    }

    return b.id - a.id;
}

/**
 * Регистрирует обработчик глобального действия `App Back`.
 * @param handler обработчик, возвращающий `true`, если действие обработано
 * @param options настройки приоритета обработки
 * @returns функцию снятия регистрации
 */
export function registerAppBackHandler(handler: AppBackHandler, options: AppBackHandlerOptions = {}): () => void {
    const entry: AppBackEntry = {
        id: nextId,
        priority: options.priority ?? DEFAULT_PRIORITY,
        handler,
    };

    nextId += 1;
    entries = [...entries, entry];

    return () => {
        entries = entries.filter((item) => item.id !== entry.id);
    };
}

/**
 * Регистрирует router fallback для `App Back`.
 * @param handler обработчик перехода назад по маршрутам
 */
export function setRouterBackHandler(handler: (() => void) | null): void {
    routerBack = handler;
}

/**
 * Выполняет единое действие приложения «Назад»: transient UI → overlay → legacy adapters → router.
 */
export function runAppBack(): void {
    const orderedEntries = [...entries].sort(sortByPriorityAndRegistration);

    for (const entry of orderedEntries) {
        if (entry.handler()) {
            return;
        }
    }

    routerBack?.();
}
